import { ILocalizedError, IPaymentAccountStatus, IPaymentRedirect } from '@localina/core';
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions, useQueryClient } from '@tanstack/react-query';
import { LocalinaApiContext } from '../../../index';
import { useRestaurantId } from '../../utils/RestaurantUtils';
import { queryKeys } from './query-keys';

// No-Show Protection: the restaurant's payment account (Settings -> Integrations -> Payments).

/** Read live from the payment provider by the backend on every request. */
const usePaymentAccount = (
    options?: UseQueryOptions<
        IPaymentAccountStatus,
        ILocalizedError,
        IPaymentAccountStatus,
        ReturnType<typeof queryKeys.restaurants.single.payments.account>
    >,
) => {
    const restaurantId = useRestaurantId();
    return useQuery(
        queryKeys.restaurants.single.payments.account(restaurantId),
        () => LocalinaApiContext.serviceApi.getPaymentAccount(restaurantId),
        options,
    );
};

const useCreatePaymentAccountMutation = (
    options?: UseMutationOptions<IPaymentAccountStatus, ILocalizedError, { displayName: string; contactEmail: string }>,
) => {
    const queryClient = useQueryClient();
    const restaurantId = useRestaurantId();
    return useMutation({
        mutationFn: (variables) => LocalinaApiContext.serviceApi.createPaymentAccount(restaurantId, variables),
        onSuccess: (status) =>
            queryClient.setQueryData(queryKeys.restaurants.single.payments.account(restaurantId), status),
        ...options,
    });
};

/** Creates a single-use link to the provider's onboarding. The caller redirects the browser to it. */
const useCreatePaymentAccountLinkMutation = (options?: UseMutationOptions<IPaymentRedirect, ILocalizedError>) => {
    const restaurantId = useRestaurantId();
    return useMutation({
        mutationFn: () => LocalinaApiContext.serviceApi.createPaymentAccountLink(restaurantId),
        ...options,
    });
};

export { usePaymentAccount, useCreatePaymentAccountMutation, useCreatePaymentAccountLinkMutation };
